import type { Locale } from "@/lib/i18n/config";

import { COOKIE_CONSENT_COOKIE_NAME } from "./categories";

export type CookieInventoryCategory =
  | "necessary"
  | "analytics"
  | "marketing";

export type CookieInventoryItem = {
  category: CookieInventoryCategory;
  name: string;
  provider: string;
  purpose: string;
  duration: string;
};

type LocalizedText = Record<Locale, string>;

type CookieInventoryDefinition = {
  category: CookieInventoryCategory;
  name: string;
  provider: string;
  purpose: LocalizedText;
  duration: LocalizedText;
};

export type CookieTableLabels = {
  title: string;
  category: string;
  name: string;
  provider: string;
  purpose: string;
  duration: string;
};

const text = (values: LocalizedText, locale: Locale) =>
  values[locale] || values.en;

const sixMonths: LocalizedText = {
  en: "6 months",
  es: "6 meses",
  ca: "6 mesos",
  de: "6 Monate",
  fr: "6 mois",
  it: "6 mesi",
  pt: "6 meses",
};

const fifteenMinutes: LocalizedText = {
  en: "15 minutes",
  es: "15 minutos",
  ca: "15 minuts",
  de: "15 Minuten",
  fr: "15 minutes",
  it: "15 minuti",
  pt: "15 minutos",
};

const thirtyDays: LocalizedText = {
  en: "30 days",
  es: "30 días",
  ca: "30 dies",
  de: "30 Tage",
  fr: "30 jours",
  it: "30 giorni",
  pt: "30 dias",
};

const sessionToThirtyDays: LocalizedText = {
  en: "Session to 30 days",
  es: "Sesión a 30 días",
  ca: "Sessió a 30 dies",
  de: "Sitzung bis 30 Tage",
  fr: "Session à 30 jours",
  it: "Sessione fino a 30 giorni",
  pt: "Sessão até 30 dias",
};

const oneYear: LocalizedText = {
  en: "1 year",
  es: "1 ano",
  ca: "1 any",
  de: "1 Jahr",
  fr: "1 an",
  it: "1 anno",
  pt: "1 ano",
};

const upToTwoYears: LocalizedText = {
  en: "Up to 2 years",
  es: "Hasta 2 años",
  ca: "Fins a 2 anys",
  de: "Bis zu 2 Jahre",
  fr: "Jusqu'à 2 ans",
  it: "Fino a 2 anni",
  pt: "Até 2 anos",
};

const minutesToTwentyFourHours: LocalizedText = {
  en: "Minutes to 24 hours",
  es: "Minutos a 24 horas",
  ca: "Minuts a 24 hores",
  de: "Minuten bis 24 Stunden",
  fr: "Minutes à 24 heures",
  it: "Minuti a 24 ore",
  pt: "Minutos a 24 horas",
};

const sessionToOneYear: LocalizedText = {
  en: "Session to 1 year",
  es: "Sesión a 1 año",
  ca: "Sessió a 1 any",
  de: "Sitzung bis 1 Jahr",
  fr: "Session à 1 an",
  it: "Sessione fino a 1 anno",
  pt: "Sessão até 1 ano",
};

const upToThreeMonths: LocalizedText = {
  en: "Up to 3 months",
  es: "Hasta 3 meses",
  ca: "Fins a 3 mesos",
  de: "Bis zu 3 Monate",
  fr: "Jusqu'à 3 mois",
  it: "Fino a 3 mesi",
  pt: "Até 3 meses",
};

const COOKIE_INVENTORY: CookieInventoryDefinition[] = [
  {
    category: "necessary",
    name: COOKIE_CONSENT_COOKIE_NAME,
    provider: "Alpha Strategy",
    purpose: {
      en: "Stores the visitor's cookie consent choices.",
      es: "Guarda las preferencias de consentimiento de cookies del visitante.",
      ca: "Desa les preferències de consentiment de cookies del visitant.",
      de: "Speichert die Cookie-Einwilligungsentscheidungen des Besuchers.",
      fr: "Enregistre les choix de consentement aux cookies du visiteur.",
      it: "Memorizza le scelte di consenso ai cookie del visitatore.",
      pt: "Guarda as escolhas de consentimento de cookies do visitante.",
    },
    duration: sixMonths,
  },
  {
    category: "necessary",
    name: "token",
    provider: "Alpha Strategy",
    purpose: {
      en: "Keeps an authenticated user session active.",
      es: "Mantiene activa la sesión de un usuario autenticado.",
      ca: "Manté activa la sessió d'un usuari autenticat.",
      de: "Hält die Sitzung eines angemeldeten Benutzers aktiv.",
      fr: "Maintient active la session d'un utilisateur authentifié.",
      it: "Mantiene attiva la sessione di un utente autenticato.",
      pt: "Mantém ativa a sessão de um utilizador autenticado.",
    },
    duration: fifteenMinutes,
  },
  {
    category: "necessary",
    name: "refresh-token",
    provider: "Alpha Strategy",
    purpose: {
      en: "Refreshes the authenticated user session.",
      es: "Renueva la sesión del usuario autenticado.",
      ca: "Renova la sessió de l'usuari autenticat.",
      de: "Erneuert die Sitzung des angemeldeten Benutzers.",
      fr: "Renouvelle la session de l'utilisateur authentifié.",
      it: "Rinnova la sessione dell'utente autenticato.",
      pt: "Renova a sessão do utilizador autenticado.",
    },
    duration: thirtyDays,
  },
  {
    category: "necessary",
    name: "role",
    provider: "Alpha Strategy",
    purpose: {
      en: "Routes authenticated users to the right app area.",
      es: "Dirige a los usuarios autenticados al área correcta de la app.",
      ca: "Dirigeix els usuaris autenticats a l'àrea correcta de l'app.",
      de: "Leitet angemeldete Benutzer in den passenden App-Bereich.",
      fr: "Dirige les utilisateurs authentifiés vers la bonne zone de l'app.",
      it: "Indirizza gli utenti autenticati alla sezione corretta dell'app.",
      pt: "Encaminha utilizadores autenticados para a área correta da app.",
    },
    duration: thirtyDays,
  },
  {
    category: "necessary",
    name: "next-auth.*",
    provider: "NextAuth / Alpha Strategy",
    purpose: {
      en: "Supports Google sign-in and OAuth callback security.",
      es: "Da soporte al inicio de sesión con Google y a la seguridad OAuth.",
      ca: "Dona suport a l'inici de sessió amb Google i a la seguretat OAuth.",
      de: "Unterstützt Google-Anmeldung und OAuth-Callback-Sicherheit.",
      fr: "Prend en charge la connexion Google et la sécurité OAuth.",
      it: "Supporta l'accesso con Google e la sicurezza OAuth.",
      pt: "Suporta o início de sessão com Google e a segurança OAuth.",
    },
    duration: sessionToThirtyDays,
  },
  {
    category: "necessary",
    name: "theme",
    provider: "Alpha Strategy",
    purpose: {
      en: "Remembers the selected light or dark theme.",
      es: "Recuerda el tema claro u oscuro seleccionado.",
      ca: "Recorda el tema clar o fosc seleccionat.",
      de: "Merkt sich das ausgewählte helle oder dunkle Theme.",
      fr: "Memorise le theme clair ou sombre selectionne.",
      it: "Ricorda il tema chiaro o scuro selezionato.",
      pt: "Memoriza o tema claro ou escuro selecionado.",
    },
    duration: oneYear,
  },
  {
    category: "necessary",
    name: "locale",
    provider: "Alpha Strategy",
    purpose: {
      en: "Remembers the selected interface language.",
      es: "Recuerda el idioma seleccionado de la interfaz.",
      ca: "Recorda l'idioma seleccionat de la interfície.",
      de: "Merkt sich die ausgewählte Sprache der Benutzeroberfläche.",
      fr: "Mémorise la langue d'interface sélectionnée.",
      it: "Ricorda la lingua dell'interfaccia selezionata.",
      pt: "Memoriza o idioma selecionado da interface.",
    },
    duration: oneYear,
  },
  {
    category: "analytics",
    name: "_ga, _ga_*",
    provider: "Google Analytics",
    purpose: {
      en: "Measures site usage and product analytics.",
      es: "Mide el uso del sitio y analítica de producto.",
      ca: "Mesura l'ús del lloc i l'analítica de producte.",
      de: "Misst Websitenutzung und Produktanalyse.",
      fr: "Mesure l'utilisation du site et l'analyse produit.",
      it: "Misura l'utilizzo del sito e le analisi di prodotto.",
      pt: "Mede a utilização do site e análise de produto.",
    },
    duration: upToTwoYears,
  },
  {
    category: "analytics",
    name: "_gid, _gat*",
    provider: "Google Analytics",
    purpose: {
      en: "Distinguishes visits and throttles analytics requests.",
      es: "Distingue visitas y limita solicitudes de analítica.",
      ca: "Distingeix visites i limita sol·licituds d'analítica.",
      de: "Unterscheidet Besuche und begrenzt Analyseanfragen.",
      fr: "Distingue les visites et limite les requêtes d'analyse.",
      it: "Distingue le visite e limita le richieste di analisi.",
      pt: "Distingue visitas e limita pedidos de análise.",
    },
    duration: minutesToTwentyFourHours,
  },
  {
    category: "analytics",
    name: "_clck, _clsk",
    provider: "Microsoft Clarity",
    purpose: {
      en: "Measures product usage and session diagnostics when configured.",
      es: "Mide el uso del producto y diagnósticos de sesión si está configurado.",
      ca: "Mesura l'ús del producte i diagnòstics de sessió si està configurat.",
      de: "Misst Produktnutzung und Sitzungsdiagnosen, wenn konfiguriert.",
      fr: "Mesure l'utilisation du produit et les diagnostics de session si configuré.",
      it: "Misura l'uso del prodotto e la diagnostica di sessione se configurato.",
      pt: "Mede a utilização do produto e diagnósticos de sessão quando configurado.",
    },
    duration: sessionToOneYear,
  },
  {
    category: "marketing",
    name: "_fbp, _fbc",
    provider: "Meta",
    purpose: {
      en: "Measures advertising performance and attribution.",
      es: "Mide rendimiento publicitario y atribución.",
      ca: "Mesura el rendiment publicitari i l'atribució.",
      de: "Misst Werbeleistung und Attribution.",
      fr: "Mesure la performance publicitaire et l'attribution.",
      it: "Misura performance pubblicitaria e attribuzione.",
      pt: "Mede desempenho publicitário e atribuição.",
    },
    duration: upToThreeMonths,
  },
  {
    category: "marketing",
    name: "_rdt_uuid",
    provider: "Reddit",
    purpose: {
      en: "Measures Reddit advertising performance and attribution.",
      es: "Mide rendimiento y atribución de publicidad en Reddit.",
      ca: "Mesura el rendiment i l'atribució de publicitat a Reddit.",
      de: "Misst Reddit-Werbeleistung und Attribution.",
      fr: "Mesure la performance publicitaire Reddit et l'attribution.",
      it: "Misura performance pubblicitaria Reddit e attribuzione.",
      pt: "Mede desempenho e atribuição de publicidade no Reddit.",
    },
    duration: upToThreeMonths,
  },
  {
    category: "marketing",
    name: "alpha_collab_*",
    provider: "Alpha Strategy",
    purpose: {
      en: "Persists collaboration attribution after marketing consent.",
      es: "Conserva la atribución de colaboraciones tras aceptar marketing.",
      ca: "Conserva l'atribució de col·laboracions després d'acceptar màrqueting.",
      de: "Speichert Partner-Attribution nach Marketing-Einwilligung.",
      fr: "Conserve l'attribution des collaborations après consentement marketing.",
      it: "Conserva l'attribuzione delle collaborazioni dopo il consenso marketing.",
      pt: "Mantém a atribuição de colaborações após consentimento de marketing.",
    },
    duration: thirtyDays,
  },
];

export const getCookieInventory = (locale: Locale): CookieInventoryItem[] =>
  COOKIE_INVENTORY.map((item) => ({
    category: item.category,
    name: item.name,
    provider: item.provider,
    purpose: text(item.purpose, locale),
    duration: text(item.duration, locale),
  }));

export const getCookieCategoryLabels = (
  locale: Locale
): Record<CookieInventoryCategory, string> => ({
  necessary: text(
    {
      en: "Necessary",
      es: "Necesarias",
      ca: "Necessàries",
      de: "Notwendig",
      fr: "Nécessaires",
      it: "Necessari",
      pt: "Necessários",
    },
    locale
  ),
  analytics: text(
    {
      en: "Analytics",
      es: "Analítica",
      ca: "Analítica",
      de: "Analyse",
      fr: "Analyse",
      it: "Analisi",
      pt: "Análise",
    },
    locale
  ),
  marketing: text(
    {
      en: "Marketing",
      es: "Marketing",
      ca: "Màrqueting",
      de: "Marketing",
      fr: "Marketing",
      it: "Marketing",
      pt: "Marketing",
    },
    locale
  ),
});

export const getCookieTableLabels = (locale: Locale): CookieTableLabels => ({
  title: text(
    {
      en: "Cookie list",
      es: "Lista de cookies",
      ca: "Llista de cookies",
      de: "Cookie-Liste",
      fr: "Liste des cookies",
      it: "Elenco dei cookie",
      pt: "Lista de cookies",
    },
    locale
  ),
  category: text(
    {
      en: "Category",
      es: "Categoría",
      ca: "Categoria",
      de: "Kategorie",
      fr: "Catégorie",
      it: "Categoria",
      pt: "Categoria",
    },
    locale
  ),
  name: text(
    {
      en: "Name",
      es: "Nombre",
      ca: "Nom",
      de: "Name",
      fr: "Nom",
      it: "Nome",
      pt: "Nome",
    },
    locale
  ),
  provider: text(
    {
      en: "Provider",
      es: "Proveedor",
      ca: "Proveïdor",
      de: "Anbieter",
      fr: "Fournisseur",
      it: "Fornitore",
      pt: "Fornecedor",
    },
    locale
  ),
  purpose: text(
    {
      en: "Purpose",
      es: "Finalidad",
      ca: "Finalitat",
      de: "Zweck",
      fr: "Finalité",
      it: "Finalità",
      pt: "Finalidade",
    },
    locale
  ),
  duration: text(
    {
      en: "Duration",
      es: "Duración",
      ca: "Durada",
      de: "Dauer",
      fr: "Durée",
      it: "Durata",
      pt: "Duração",
    },
    locale
  ),
});
